import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GoldenSigil } from "@/components/GoldenSigil";
import { SimpleGhostButton as GhostButton, SimpleCircleOption as CircleOption } from "@/components/SimpleButtons";
import { ProgressIndicator } from "@/components/ProgressIndicator";
import { GoldLineInput } from "@/components/GoldLineInput";
import TarotReader from "@/components/TarotReader";
import { CalibrationScreen } from "@/components/CalibrationScreen";
import { ConclaveMarketplace } from "@/components/ConclaveMarketplace";
import { ChatInterface } from "@/components/ChatInterface";
import { StarfieldBackground } from "@/components/StarfieldBackground";
import { LocationAutocomplete } from "@/components/LocationAutocomplete";
import { TimeOfDaySelector } from "@/components/TimeOfDaySelector";
import { ArmillarySphere } from "@/components/ArmillarySphere";
import { FloatingTarotCards } from "@/components/FloatingTarotCards";
import { GoldenQuill } from "@/components/GoldenQuill";
import { MeditationAura } from "@/components/MeditationAura";
import { OracleCards } from "@/components/OracleCards";
import { CelestialDatePicker, CelestialTimePicker } from "@/components/CelestialTumbler";
import { GoldenLantern } from "@/components/GoldenLantern";
import { SanctuaryDashboard } from "@/components/SanctuaryDashboard";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { useAuthGatekeeper } from "@/components/AuthGatekeeper";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigation, useBackHandler, ROUTES } from "@/contexts/NavigationContext";
import ClaimDestinyModal from "@/components/ClaimDestinyModal";
import { LegalLinks } from "@/components/LegalPages";
import { ChevronLeft } from "lucide-react";
import { getAuthHeaders } from "@/utils/api";

// Minimalist back button for onboarding steps
const StepBackButton = ({ onClick, show = true }) => {
    if (!show) return null;
    return (
        <motion.button
            onClick={onClick}
            className="absolute top-4 left-4 flex items-center gap-1 text-primary/40 hover:text-primary/70 transition-colors group z-20"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            data-testid="step-back-btn"
        >
            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            <span className="text-xs font-inter tracking-wide uppercase">Back</span>
        </motion.button>
    );
};

// localStorage keys
const STORAGE_KEY = "astral_belonging_onboarding";


// Animation variants
const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0, transition: { duration: 0.3 } }
};

const questionVariants = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -30, transition: { duration: 0.3 } }
};

// Custom hook for back button handling in PWA during onboarding
const useBackButton = (currentStep, setCurrentStep, onExit) => {
    const hasInitializedRef = useRef(false);
    
    useEffect(() => {
        const handleBackButton = (e) => {
            // If not on welcome screen, go back a step
            if (currentStep > 0) {
                e.preventDefault();
                setCurrentStep(prev => prev - 1);
            } else {
                // On welcome screen, call onExit
                if (onExit) onExit();
            }
        };

        // Listen for popstate (browser/Android back button)
        window.addEventListener('popstate', handleBackButton);
        
        // Push initial state only once
        if (!hasInitializedRef.current) {
            window.history.pushState({ step: 0, type: 'onboarding' }, '', window.location.href);
            hasInitializedRef.current = true;
        }

        return () => {
            window.removeEventListener('popstate', handleBackButton);
        };
    }, [currentStep, setCurrentStep, onExit]);
    
    // Push state when step changes (except initial)
    useEffect(() => {
        if (hasInitializedRef.current && currentStep > 0) {
            window.history.pushState({ step: currentStep, type: 'onboarding' }, '', window.location.href);
        }
    }, [currentStep]);
};

export const OnboardingFlow = ({ initialSection }) => {
    // Steps: 0 = Welcome, 1-4 = Questions, 5 = Palm, 6 = Calibration, 7 = Marketplace, 8 = Chat
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [showCalibration, setShowCalibration] = useState(false);
    const [showMarketplace, setShowMarketplace] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const [selectedGuide, setSelectedGuide] = useState(null);
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [isReturningUser, setIsReturningUser] = useState(false);
    
    // Deep linking: active overlay sections
    const [activeSection, setActiveSection] = useState(null); // 'tarot', 'orbit', 'dreams', 'settings', 'profile'
    
    // Navigation context for deep linking
    const { pushRoute, goBack, registerBackHandler } = useNavigation();
    
    // Translations
    const { t, language, setLanguage } = useLanguage();
    
    // Google Auth hook (legacy)
    const { signIn, user: googleUser, isLoading: authLoading, error: authError, isInitialized: googleReady } = useGoogleAuth();
    
    // Global Auth from AuthGatekeeper (handles Firebase auth check at app level)
    const { 
        user: gatekeeperUser, 
        profile: gatekeeperProfile,
        isAuthenticated,
        logout: gatekeeperLogout 
    } = useAuthGatekeeper();
    
    // Form data
    // Note: languageContext is initialized with browser language but will be 
    // synced to LanguageContext when profile is saved (see handleProfileSave)
    const [userData, setUserData] = useState({
        name: "",
        birthDate: "",
        location: "",
        // Coordinates from Google Places API (for accurate calculations)
        locationLat: null,
        locationLng: null,
        utcOffsetMinutes: null,
        // Other fields
        birthTime: "",
        timeOfDay: "",
        unknownTime: false,
        mood: "",
        focus: "",
        palmImage: null,
        // Gender identity for inclusive AI context
        genderIdentity: "", // "male", "female", "non-binary"
        // Language - synced from LanguageContext on profile save
        languageContext: localStorage.getItem('astrum_language') || navigator.language || "en",
        // Oracle's palm revelation (for Learning Core continuity)
        lastRevelation: null,
        // Tarot reading data
        tarotCards: null,
        tarotReading: null,
        // Avatar for settings panel
        avatarUrl: null,
        // Computed signs
        sunSign: null,
        moonSign: null,
        risingSign: null,
        // Personal details for Orbits
        hobbies: [],
        occupation: "",
        romanticInterests: ""
    });

    // Determine if back button should be shown during onboarding
    // Hide for authenticated users without profile (they must complete onboarding)
    const hasCompleteProfile = !!(gatekeeperProfile && gatekeeperProfile.birth_date);
    const showBackButton = !isAuthenticated || hasCompleteProfile;

    // Deep linking: Handle initial section from URL route
    useEffect(() => {
        if (initialSection && showMarketplace) {
            // Only apply deep link if user is on dashboard
            if (initialSection === 'tarot') {
                setActiveSection('tarot');
            } else if (initialSection === 'settings') {
                setActiveSection('settings');
            }
            // Note: orbit, dreams are part of dashboard scroll, not separate views
        }
    }, [initialSection, showMarketplace]);

    // Register back handler for active sections - memoized to prevent infinite loops
    const sectionBackHandler = useCallback(() => {
        if (activeSection) {
            // Close the active section and go back to dashboard
            setActiveSection(null);
            pushRoute(ROUTES.DASHBOARD, { replace: true });
            return true; // Handled
        }
        if (showChat) {
            // Go back from chat to dashboard
            setShowChat(false);
            setSelectedGuide(null);
            setShowMarketplace(true);
            pushRoute(ROUTES.DASHBOARD, { replace: true });
            return true;
        }
        return false; // Not handled, let default behavior occur
    }, [activeSection, showChat, pushRoute]);
    
    useBackHandler(sectionBackHandler, 10); // High priority

    // Back button handler for PWA (onboarding steps)
    useBackButton(currentStep, setCurrentStep, () => {
        // On welcome screen, do nothing (or show exit toast)
        console.log("[NAV] At welcome screen - back button ignored");
    });

    // Use AuthGatekeeper data to initialize if user is already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            if (gatekeeperProfile && gatekeeperProfile.birth_date) {
                // User has completed profile → go to dashboard
                console.log('🔐 Gatekeeper: User authenticated WITH profile, going to dashboard');
                // Restore user data from gatekeeper profile
                setUserData({
                    name: gatekeeperProfile.name || gatekeeperUser?.name || "",
                    birthDate: gatekeeperProfile.birth_date || "",
                    location: gatekeeperProfile.location || "",
                    locationLat: gatekeeperProfile.location_lat || null,
                    locationLng: gatekeeperProfile.location_lng || null,
                    utcOffsetMinutes: gatekeeperProfile.utc_offset_minutes || null,
                    birthTime: gatekeeperProfile.birth_time || "",
                    timeOfDay: gatekeeperProfile.time_of_day || "",
                    unknownTime: gatekeeperProfile.unknown_time || false,
                    mood: gatekeeperProfile.mood || "",
                    focus: gatekeeperProfile.focus || "",
                    palmImage: gatekeeperProfile.palm_image || null,
                    // Avatar URL for settings panel icon
                    avatarUrl: gatekeeperProfile.avatar_url || null,
                    // Additional profile fields
                    genderIdentity: gatekeeperProfile.gender_identity || "",
                    sunSign: gatekeeperProfile.sun_sign || null,
                    moonSign: gatekeeperProfile.moon_sign || null,
                    risingSign: gatekeeperProfile.rising_sign || null,
                    hobbies: gatekeeperProfile.hobbies || [],
                    occupation: gatekeeperProfile.occupation || "",
                    romanticInterests: gatekeeperProfile.romantic_interests || ""
                });
                
                // Language is already restored by AuthGatekeeper via localStorage
                // No need to call setLanguage here (it causes backend sync issues)
                
                // Skip to dashboard
                setCurrentStep(7);
                setShowMarketplace(true);
            } else {
                // User is authenticated but NO profile → start onboarding at Step 1
                console.log('🔐 Gatekeeper: User authenticated but NO profile, starting onboarding');
                setCurrentStep(1); // Start at name input (skip welcome screen)
                setShowMarketplace(false);
            }
        }
    }, [isAuthenticated, gatekeeperProfile, gatekeeperUser]);

    // Load from localStorage on mount (for guest data only)
    useEffect(() => {
        // Skip if already authenticated (handled by gatekeeper)
        if (isAuthenticated) return;
        
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Restore userData with proper defaults
                const savedUserData = parsed.userData || {};
                setUserData({
                    name: savedUserData.name || "",
                    birthDate: savedUserData.birthDate || "",
                    location: savedUserData.location || "",
                    // Restore coordinates if saved
                    locationLat: savedUserData.locationLat || null,
                    locationLng: savedUserData.locationLng || null,
                    utcOffsetMinutes: savedUserData.utcOffsetMinutes || null,
                    // Other fields
                    birthTime: savedUserData.birthTime || "",
                    timeOfDay: savedUserData.timeOfDay || "",
                    unknownTime: savedUserData.unknownTime || false,
                    mood: savedUserData.mood || "",
                    focus: savedUserData.focus || "",
                    palmImage: savedUserData.palmImage || null
                });
                setCurrentStep(parsed.currentStep || 0);
                setCompletedSteps(parsed.completedSteps || []);
                // If we've completed onboarding (step 7 or showMarketplace was true), show marketplace
                if (parsed.currentStep >= 7 || parsed.showMarketplace) {
                    setShowMarketplace(true);
                }
            } catch (e) {
                console.error("Failed to parse saved data:", e);
            }
        }
    }, []);

    // Save to localStorage on changes - include showMarketplace state
    useEffect(() => {
        // Only save if we have actual data or have progressed
        if (currentStep > 0 || showMarketplace || Object.values(userData).some(v => v)) {
            const dataToSave = {
                userData,
                currentStep,
                completedSteps,
                showMarketplace
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
        }
    }, [userData, currentStep, completedSteps, showMarketplace]);

    const handleEnterCircle = () => {
        setCurrentStep(1);
    };

    const handleNextStep = () => {
        setCompletedSteps(prev => [...prev, currentStep]);
        setCurrentStep(prev => prev + 1);
    };

    // Handle Oracle Eye ritual completion (with palm image and revelation)
    const handleOracleComplete = async (result) => {
        const { image, revelation } = result;
        setUserData(prev => ({ 
            ...prev, 
            palmImage: image,
            lastRevelation: revelation  // Save Oracle's reading for Learning Core
        }));
        setCompletedSteps(prev => [...prev, 6]);
        setTimeout(() => {
            setShowCalibration(true);
        }, 500);
    };

    // Handle Oracle skip - "Proceed in Shadows" - save shadow message
    const handleOracleSkip = (shadowMessage) => {
        setUserData(prev => ({ 
            ...prev, 
            palmImage: null,
            lastRevelation: shadowMessage  // Save the shadow message for Learning Core
        }));
        setCompletedSteps(prev => [...prev, 6]);
        setTimeout(() => {
            setShowCalibration(true);
        }, 300);
    };

    // Legacy handlers (kept for compatibility)
    const handlePalmCapture = (palmImage) => {
        setUserData(prev => ({ ...prev, palmImage }));
        setCompletedSteps(prev => [...prev, 5]);
        setTimeout(() => {
            setShowCalibration(true);
        }, 500);
    };

    // Handle skip ritual - proceed without palm image
    const handleSkipRitual = () => {
        setCompletedSteps(prev => [...prev, 5]);
        setTimeout(() => {
            setShowCalibration(true);
        }, 300);
    };

    // Handle profile reset - clear all data and restart onboarding
    const handleProfileReset = useCallback(() => {
        console.log('🔄 Profile reset triggered');
        
        // Clear localStorage
        localStorage.removeItem(STORAGE_KEY);
        
        // Preserve current language setting
        const currentLanguage = language || localStorage.getItem('astrum_language') || navigator.language || "en";
        
        // Reset all state but preserve language
        setUserData({
            name: "",
            birthDate: "",
            location: "",
            locationLat: null,
            locationLng: null,
            utcOffsetMinutes: null,
            birthTime: "",
            timeOfDay: "",
            unknownTime: false,
            mood: "",
            focus: "",
            palmImage: null,
            lastRevelation: null,
            genderIdentity: "",
            languageContext: currentLanguage // Preserve user's language preference
        });
        setCurrentStep(0);
        setCompletedSteps([]);
        setShowCalibration(false);
        setShowMarketplace(false);
        setShowChat(false);
        
        console.log('✨ Profile reset complete - language preserved:', currentLanguage);
    }, [language]);

    // Handle logout - clear Firebase session, local data, and redirect to start
    const handleLogout = useCallback(async () => {
        console.log('🚪 Logout triggered');
        
        await gatekeeperLogout(() => {
            // Clear all local state
            setUserData({
                name: "",
                birthDate: "",
                location: "",
                locationLat: null,
                locationLng: null,
                utcOffsetMinutes: null,
                birthTime: "",
                timeOfDay: "",
                unknownTime: false,
                mood: "",
                focus: "",
                palmImage: null
            });
            setCurrentStep(0);
            setCompletedSteps([]);
            setShowCalibration(false);
            setShowMarketplace(false);
            setShowChat(false);
            setShowClaimModal(false);
            
            console.log('✨ Logout complete - returning to start');
        });
    }, [gatekeeperLogout]);

    const handleCalibrationComplete = async () => {
        setShowCalibration(false);
        
        // Check if user is already authenticated
        if (googleUser || isAuthenticated) {
            // User is authenticated - save profile and go to dashboard
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                
                // Extract location display string (supports both object and string formats)
                const locationDisplay = typeof userData.location === 'object' 
                    ? (userData.location.display || '') 
                    : (userData.location || '');
                
                // Get coordinates - prefer top-level userData fields, fallback to location object
                const lat = userData.locationLat || (typeof userData.location === 'object' ? userData.location.lat : null);
                const lng = userData.locationLng || (typeof userData.location === 'object' ? userData.location.lng : null);
                const utcOffset = userData.utcOffsetMinutes ?? (typeof userData.location === 'object' ? userData.location.utc_offset_minutes : null);
                
                console.log('📍 Saving profile with location:', locationDisplay);
                console.log('📍 Coordinates:', lat, lng, 'UTC offset:', utcOffset);
                console.log('🌐 Selected language from LanguageContext:', language);
                console.log('🌐 Navigator language:', navigator.language);
                console.log('🌐 Language being sent to backend:', language || navigator.language || "en");
                
                // Use proper auth headers (Firebase ID token or session token)
                const authHeaders = await getAuthHeaders();
                console.log('🔑 Auth headers present:', !!authHeaders['Authorization']);
                
                const profileResponse = await fetch(`${backendUrl}/api/profile`, {
                    method: 'POST',
                    headers: authHeaders,
                    credentials: 'include',
                    body: JSON.stringify({
                        name: userData.name,
                        birth_date: userData.birthDate,
                        // Location fields - send coordinates directly from frontend
                        location: locationDisplay,
                        location_lat: lat,
                        location_lng: lng,
                        utc_offset_minutes: utcOffset,
                        // Other fields
                        birth_time: userData.birthTime || null,
                        time_of_day: userData.timeOfDay || null,
                        unknown_time: userData.unknownTime,
                        mood: userData.mood || null,
                        focus: userData.focus || null,
                        palm_image: userData.palmImage || null,
                        // Gender identity for inclusive AI context
                        gender_identity: userData.genderIdentity || null,
                        // Enhanced onboarding data for Learning Core
                        heart_feeling: userData.mood || null,  // Map mood to heart_feeling
                        shadow_work: userData.focus || null,   // Map focus to shadow_work
                        // User's selected language - ALWAYS use current app language, never browser default
                        language_context: language,
                        // Oracle's palm revelation - critical for Learning Core continuity
                        last_revelation: userData.lastRevelation || null
                    }),
                });
                
                if (profileResponse.ok) {
                    const savedProfile = await profileResponse.json();
                    console.log('✨ Profile saved successfully:', savedProfile);
                } else {
                    const errorText = await profileResponse.text();
                    console.error('❌ Profile save failed:', profileResponse.status, errorText);
                }
            } catch (err) {
                console.error('❌ Failed to save profile to backend:', err);
                // Continue anyway - localStorage has the data
            }
            
            setCurrentStep(7);
            setShowMarketplace(true);
        } else {
            // User is a guest - show "Claim Your Destiny" modal
            console.log('👤 Guest user - showing Claim Destiny modal');
            setShowClaimModal(true);
        }
    };

    // Handle successful authentication from ClaimDestinyModal
    const handleClaimSuccess = async (authUser) => {
        console.log('✅ User claimed their destiny:', authUser);
        setShowClaimModal(false);
        
        // Guest data was already migrated by the backend during auth
        // Reload the page to let AuthGatekeeper handle the authenticated state
        window.location.reload();
    };

    // Skip claim and continue as guest
    // NOTE: This should ONLY be called when explicitly clicking a "Continue as Guest" button
    // Not when closing the modal or clicking backdrop
    const handleSkipClaim = () => {
        console.log('⏭️ User skipped claim - continuing as guest');
        
        // ANCHOR GUEST STATE IMMEDIATELY - Don't wait for useEffect
        // This prevents the kickback loop by setting completion flag synchronously
        const guestData = {
            userData,
            currentStep: 7,
            completedSteps,
            showMarketplace: true,
            isGuest: true // Mark as guest explicitly
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(guestData));
        console.log('🔒 Guest state anchored to localStorage immediately');
        
        setShowClaimModal(false);
        setIsReturningUser(false);
        // Only proceed to marketplace if not a returning user (new users skip claim)
        if (!isReturningUser) {
            setCurrentStep(7);
            setShowMarketplace(true);
        }
        // Returning users just close the modal and stay on welcome screen
    };
    
    // Handle modal close - DON'T allow bypassing auth for new users after tarot
    const handleClaimModalClose = () => {
        // If returning user, they can close and go back to welcome
        if (isReturningUser) {
            setShowClaimModal(false);
            return;
        }
        // For new users after tarot - don't allow closing without auth or explicit guest choice
        // Modal stays open - user must either sign in or click "Continue as Guest"
        console.log('🚫 New user cannot close auth modal - must authenticate or choose guest');
    };

    const handleSelectGuide = (guide) => {
        setSelectedGuide(guide);
        setShowMarketplace(false);
        setShowChat(true);
        setCurrentStep(8);
    };

    const handleBackToSanctuary = () => {
        // Go back to beginning for "My Sanctuary"
        resetOnboarding();
    };

    const handleBackFromChat = () => {
        setShowChat(false);
        setSelectedGuide(null);
        setShowMarketplace(true);
        setCurrentStep(7);
        pushRoute(ROUTES.DASHBOARD, { replace: true });
    };

    // Deep linking navigation handlers
    const navigateToSection = useCallback((section) => {
        setActiveSection(section);
        // Push to browser history
        const routeMap = {
            'tarot': ROUTES.TAROT,
            'settings': ROUTES.SETTINGS,
            'profile': ROUTES.PROFILE
        };
        if (routeMap[section]) {
            pushRoute(routeMap[section]);
        }
    }, [pushRoute]);

    const closeActiveSection = useCallback(() => {
        setActiveSection(null);
        pushRoute(ROUTES.DASHBOARD, { replace: true });
    }, [pushRoute]);

    const resetOnboarding = () => {
        localStorage.removeItem(STORAGE_KEY);
        setUserData({
            name: "",
            birthDate: "",
            location: "",
            birthTime: "",
            timeOfDay: "",
            unknownTime: false,
            mood: "",
            focus: "",
            palmImage: null
        });
        setCurrentStep(0);
        setCompletedSteps([]);
        setShowCalibration(false);
        setShowMarketplace(false);
        setShowChat(false);
        setSelectedGuide(null);
    };

    // Mood options
    const moodOptions = [
        { icon: "🌫️", label: t('moods.heavy'), value: "heavy" },
        { icon: "🔥", label: t('moods.restless'), value: "restless" },
        { icon: "✨", label: t('moods.ready'), value: "ready" },
        { icon: "🌑", label: t('moods.quiet'), value: "quiet" }
    ];

    // Focus options - Mystical paths with tarot-style presentation
    const focusOptions = [
        { icon: "💗", label: t('focus.hearts_desire'), value: "hearts-desire" },
        { icon: "⚔️", label: t('focus.sacred_purpose'), value: "sacred-purpose" },
        { icon: "🌙", label: t('focus.shadow_work'), value: "shadow-work" }
    ];

    // Render calibration screen overlay
    if (showCalibration) {
        return (
            <CalibrationScreen 
                palmImage={userData.palmImage}
                userData={userData}
                onComplete={handleCalibrationComplete} 
            />
        );
    }

    // Render chat interface
    if (showChat && selectedGuide) {
        return (
            <ChatInterface
                guide={selectedGuide}
                userData={userData}
                onBack={handleBackFromChat}
            />
        );
    }

    // NOTE: Auth check loading is now handled by AuthGatekeeper at app root level
    // This component will NOT render until auth check is complete

    // THE 'READY' GUARD: Do not render SanctuaryDashboard until auth is confirmed
    // This prevents 401 errors from firing API calls before session_token is set
    const isAuthReady = () => {
        // If user claims to be authenticated, verify token exists
        if (isAuthenticated) {
            const token = localStorage.getItem('session_token');
            if (!token) {
                console.warn('⚠️ OnboardingFlow: isAuthenticated=true but no session_token found');
                return false;
            }
            return true;
        }
        // Guest users are always "ready" (no token needed)
        return true;
    };

    // Render Sanctuary Dashboard after onboarding
    if (showMarketplace) {
        // READY GUARD: Wait for auth to be fully ready before rendering dashboard
        if (!isAuthReady()) {
            console.log('⏳ OnboardingFlow: Waiting for auth to be ready before rendering dashboard');
            // Return a loading state while we wait for auth
            return (
                <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
                    <div className="text-primary/70 font-cinzel text-lg tracking-widest animate-pulse">
                        Preparing your sanctuary...
                    </div>
                </div>
            );
        }
        
        return (
            <>
                <SanctuaryDashboard
                    userData={userData}
                    onOpenChat={(oracleType, context) => {
                        // Store the oracle type if provided
                        if (oracleType) {
                            console.log('[ORACLE] Opening chat with type:', oracleType, context);
                        }
                        setShowChat(true);
                    }}
                    onReset={handleProfileReset}
                    onLogout={handleLogout}
                    isAuthenticated={isAuthenticated}
                    onNavigateToSection={navigateToSection}
                    onAvatarChange={(newAvatarUrl) => setUserData(prev => ({ ...prev, avatarUrl: newAvatarUrl }))}
                />
                
                {/* Deep Linked Section Overlays */}
                <AnimatePresence>
                    {activeSection === 'tarot' && (
                        <motion.div
                            className="fixed inset-0 z-50"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <TarotReader 
                                userName={userData.name || "Child of the Stars"}
                                userContext={{
                                    gender: userData.genderIdentity,
                                    birthDate: userData.birthDate,
                                    birthTime: userData.birthTime,
                                    location: userData.location,
                                    mood: userData.mood,
                                    focus: userData.focus
                                }}
                                onBack={closeActiveSection}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </>
        );
    }
    
    // Render Chat Interface if user opened it from Sanctuary
    if (showChat) {
        return (
            <ChatInterface
                userData={userData}
                onBack={() => setShowChat(false)}
            />
        );
    }

    // Background image for welcome screen (no longer used, kept for reference)
    const welcomeBgUrl = "https://static.prod-images.emergentagent.com/jobs/aecc395c-9cff-4c5e-8df2-7734188be02f/images/e9a5af72f0a6928555f05df2fac8d140db58d1293f2cd6bd1a1988bfa554d4fe.png";

    /**
     * Handle Native Google Sign-In (popup flow)
     * Triggers Google Account Chooser directly without page redirects
     * ONBOARDING GUARDRAIL: Check if user has profile before allowing access to Conclave
     */
    const handleLoginClick = async () => {
        // First check if user has local saved data (localStorage session)
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.showMarketplace && parsed.userData?.birthDate) {
                // User has completed onboarding locally, go to marketplace
                setShowMarketplace(true);
                return;
            }
        }
        
        // Show ClaimDestinyModal for returning users to sign in
        // This provides a unified auth experience with Google, Facebook, and Phone options
        setShowClaimModal(true);
        setIsReturningUser(true);
    };

    return (
        <div className="min-h-screen bg-obsidian-gradient relative overflow-hidden">
            {/* Show starfield for non-welcome screens */}
            {currentStep !== 0 && <StarfieldBackground />}
            
            {/* Progress indicator (shown after welcome) */}
            {currentStep > 0 && currentStep <= 5 && (
                <motion.div
                    className="fixed top-6 left-0 right-0 z-20 px-4"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <ProgressIndicator
                        totalSteps={6}
                        currentStep={currentStep - 1}
                        completedSteps={completedSteps.filter(s => s >= 1 && s <= 5).map(s => s - 1)}
                    />
                </motion.div>
            )}

            {/* Main content */}
            <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-20">
                <AnimatePresence mode="wait" initial={false}>
                    {/* Step 0: Welcome Screen - The Grand Entrance */}
                    {currentStep === 0 && (
                        <motion.div
                            key="welcome"
                            className="fixed inset-0 flex flex-col items-center justify-between text-center overflow-hidden z-20"
                            variants={pageVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ duration: 0.5 }}
                        >
                            {/* Use the SAME StarfieldBackground as onboarding screens */}
                            <StarfieldBackground />
                            
                            {/* Subtle vignette for depth */}
                            <div 
                                className="absolute inset-0 pointer-events-none z-[1]"
                                style={{
                                    background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.5) 100%)'
                                }}
                            />

                            {/* Language Selector - Top Right */}
                            <div className="absolute top-6 right-6 z-20">
                                <LanguageSelector variant="compact" />
                            </div>

                            {/* 1. BRANDING - Top Center */}
                            <motion.div 
                                className="relative z-10 pt-16 sm:pt-20"
                                initial={{ opacity: 0, y: -30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3, duration: 1 }}
                            >
                                <h1 
                                    className="font-cinzel text-4xl sm:text-5xl md:text-6xl tracking-[0.15em] font-semibold"
                                    style={{
                                        background: 'linear-gradient(135deg, #F5D76E 0%, #D4AF37 25%, #FFE4AA 50%, #D4AF37 75%, #AA8C2C 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        textShadow: '0 0 60px rgba(212, 175, 55, 0.3)',
                                        filter: 'drop-shadow(0 2px 10px rgba(212, 175, 55, 0.2))',
                                    }}
                                >
                                    {t('app_title')}
                                </h1>
                                
                                {/* Decorative line under title */}
                                <motion.div 
                                    className="mx-auto mt-4 h-px w-32 sm:w-48"
                                    style={{
                                        background: 'linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.5), transparent)'
                                    }}
                                    initial={{ scaleX: 0 }}
                                    animate={{ scaleX: 1 }}
                                    transition={{ delay: 0.8, duration: 0.8 }}
                                />
                            </motion.div>

                            {/* 2. VISUAL ANCHOR - Center - Golden Lantern (20% larger + stronger glow) */}
                            <motion.div 
                                className="relative z-10 flex-1 flex items-center justify-center"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.5, duration: 1.2 }}
                            >
                                {/* Strong radial glow behind lantern */}
                                <div 
                                    className="absolute"
                                    style={{
                                        width: '500px',
                                        height: '500px',
                                        background: 'radial-gradient(ellipse at center, rgba(255, 180, 80, 0.25) 0%, rgba(212, 175, 55, 0.1) 30%, transparent 60%)',
                                        filter: 'blur(40px)',
                                    }}
                                />
                                <div className="transform scale-[1.2]">
                                    <GoldenLantern />
                                </div>
                            </motion.div>

                            {/* 3. THE INVITATION & Actions - Bottom */}
                            <div className="relative z-10 flex flex-col items-center pb-12 sm:pb-16 px-6">
                                {/* Tagline - The Hero - BRAND SLOGAN */}
                                <motion.p
                                    className="font-cinzel text-sm sm:text-base uppercase mb-3 text-center"
                                    style={{ 
                                        color: 'rgba(212, 175, 55, 0.95)',
                                        letterSpacing: '0.12em',
                                        textShadow: '0 0 30px rgba(212, 175, 55, 0.3)'
                                    }}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1, duration: 0.8 }}
                                >
                                    {t('tagline')}
                                </motion.p>
                                
                                {/* Hero Subtitle */}
                                <motion.p
                                    className="font-inter text-xs sm:text-sm tracking-wide mb-10 max-w-xs text-center"
                                    style={{ color: 'rgba(180, 180, 190, 0.6)' }}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.1, duration: 0.8 }}
                                >
                                    {t('hero_subtitle')}
                                </motion.p>
                                
                                {/* Main Action Button - CONSULT THE ORACLE */}
                                <motion.button
                                    onClick={handleEnterCircle}
                                    className="relative px-12 py-4 rounded-sm overflow-hidden group"
                                    style={{
                                        backgroundColor: 'rgba(10, 10, 11, 0.8)',
                                        border: '1px solid #D4AF37',
                                        backdropFilter: 'blur(10px)',
                                    }}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.2, duration: 0.6 }}
                                    whileHover={{ 
                                        boxShadow: "0 0 40px rgba(212, 175, 55, 0.4)",
                                        borderColor: "#FFD700"
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                    data-testid="begin-ritual-btn"
                                >
                                    {/* Subtle inner glow on hover */}
                                    <motion.div
                                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                        style={{ 
                                            background: 'radial-gradient(ellipse at center, rgba(212, 175, 55, 0.15) 0%, transparent 70%)'
                                        }}
                                    />
                                    <span 
                                        className="relative z-10 font-inter text-sm tracking-[0.25em] uppercase"
                                        style={{ color: '#D4AF37' }}
                                    >
                                        {t('begin_ritual')}
                                    </span>
                                </motion.button>
                                
                                {/* Secondary Action - Already initiated? Enter with Google */}
                                <motion.button
                                    onClick={handleLoginClick}
                                    disabled={isSigningIn || authLoading}
                                    className="mt-6 bg-transparent border-0 cursor-pointer group disabled:cursor-wait"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.5, duration: 0.6 }}
                                    data-testid="login-link"
                                >
                                    {isSigningIn || authLoading ? (
                                        <span className="flex items-center gap-2">
                                            <span 
                                                className="w-3 h-3 border border-[rgba(212,175,55,0.5)] border-t-transparent rounded-full animate-spin"
                                            />
                                            <span 
                                                className="font-inter text-xs tracking-wide"
                                                style={{ color: 'rgba(212, 175, 55, 0.5)' }}
                                            >
                                                connecting...
                                            </span>
                                        </span>
                                    ) : (
                                        <span 
                                            className="font-inter text-xs tracking-wide transition-all duration-300 group-hover:tracking-wider"
                                            style={{ color: 'rgba(212, 175, 55, 0.5)' }}
                                        >
                                            {t('already_initiated')}{' '}
                                            <span 
                                                className="underline underline-offset-4 decoration-[rgba(212,175,55,0.3)] group-hover:decoration-[rgba(212,175,55,0.6)] transition-all duration-300"
                                                style={{ color: 'rgba(212, 175, 55, 0.7)' }}
                                            >
                                                {t('enter_with_google')}
                                            </span>
                                        </span>
                                    )}
                                </motion.button>
                                
                                {/* Auth error message */}
                                {authError && (
                                    <motion.p
                                        className="mt-4 text-red-400/70 text-xs font-inter"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                    >
                                        {authError}
                                    </motion.p>
                                )}
                                
                                {/* Legal Links */}
                                <motion.div
                                    className="mt-8"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.5 }}
                                >
                                    <LegalLinks language={language} />
                                </motion.div>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 1: The Name - Soul Signature */}
                    {currentStep === 1 && (
                        <motion.div
                            key="name"
                            className="relative flex flex-col items-center justify-center text-center w-full max-w-md z-10 bg-transparent"
                            variants={questionVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ duration: 0.5 }}
                        >
                            <StepBackButton onClick={() => setCurrentStep(0)} show={showBackButton} />
                            
                            {/* Golden Quill floating above */}
                            <GoldenQuill className="mb-4" />
                            
                            <h2 className="font-cinzel text-xl sm:text-2xl text-foreground mb-2">
                                {t('onboarding.name_title')}
                            </h2>
                            <p className="font-inter text-sm text-primary/60 mb-8 italic tracking-wide">
                                {t('onboarding.name_subtitle')}
                            </p>
                            
                            <GoldLineInput
                                placeholder={t('onboarding.name_placeholder')}
                                value={userData.name}
                                onChange={(e) => setUserData(prev => ({ ...prev, name: e.target.value }))}
                                className="mb-4"
                            />
                            
                            {/* Helper text for minimum name length */}
                            <p className={`font-inter text-xs mb-6 transition-opacity duration-300 ${
                                userData.name.trim().length > 0 && userData.name.trim().length < 3 
                                    ? 'text-primary/50' 
                                    : 'text-transparent'
                            }`}>
                                {language === 'es' ? 'Mínimo 3 caracteres' : 'At least 3 characters'}
                            </p>
                            
                            <GhostButton 
                                onClick={handleNextStep}
                                disabled={!userData.name.trim() || userData.name.trim().length < 3}
                            >
                                {t('continue')}
                            </GhostButton>
                        </motion.div>
                    )}

                    {/* Step 2: Identify Your Vessel (Gender Identity) */}
                    {currentStep === 2 && (
                        <motion.div
                            key="vessel"
                            className="relative flex flex-col items-center justify-center text-center w-full max-w-md"
                            variants={questionVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ duration: 0.6 }}
                        >
                            <StepBackButton onClick={() => setCurrentStep(1)} show={showBackButton} />
                            
                            {/* Vessel Icon */}
                            <motion.div
                                className="mb-6 text-5xl"
                                animate={{ 
                                    scale: [1, 1.05, 1],
                                    opacity: [0.8, 1, 0.8]
                                }}
                                transition={{ duration: 3, repeat: Infinity }}
                            >
                                🔮
                            </motion.div>
                            
                            <h2 className="font-cinzel text-xl sm:text-2xl text-foreground mb-2">
                                {t('onboarding.vessel_title')}
                            </h2>
                            <p className="font-inter text-sm text-primary/60 mb-8 italic tracking-wide">
                                {t('onboarding.vessel_subtitle')}
                            </p>
                            
                            {/* Gender Options */}
                            <div className="grid grid-cols-3 gap-4 w-full mb-10">
                                {[
                                    { value: 'male', label: t('onboarding.male'), icon: '♂' },
                                    { value: 'female', label: t('onboarding.female'), icon: '♀' },
                                    { value: 'other', label: t('onboarding.nonbinary'), icon: '✦' }
                                ].map(option => (
                                    <button
                                        key={option.value}
                                        onClick={() => setUserData(prev => ({ ...prev, genderIdentity: option.value }))}
                                        className="flex flex-col items-center p-4 rounded-xl transition-all"
                                        style={{
                                            background: userData.genderIdentity === option.value 
                                                ? 'rgba(212, 175, 55, 0.15)'
                                                : 'rgba(0, 0, 0, 0.2)',
                                            border: `1px solid ${userData.genderIdentity === option.value 
                                                ? 'rgba(212, 175, 55, 0.5)' 
                                                : 'rgba(212, 175, 55, 0.15)'}`,
                                            boxShadow: userData.genderIdentity === option.value 
                                                ? '0 0 20px rgba(212, 175, 55, 0.15)'
                                                : 'none'
                                        }}
                                        data-testid={`gender-${option.value}`}
                                    >
                                        <span className="text-2xl mb-2" style={{ 
                                            color: userData.genderIdentity === option.value ? '#D4AF37' : 'rgba(255,255,255,0.5)'
                                        }}>
                                            {option.icon}
                                        </span>
                                        <span className="text-xs font-inter uppercase tracking-wider" style={{
                                            color: userData.genderIdentity === option.value ? '#D4AF37' : 'rgba(255,255,255,0.4)'
                                        }}>
                                            {option.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            
                            <GhostButton 
                                onClick={handleNextStep}
                                disabled={!userData.genderIdentity}
                            >
                                {t('continue')}
                            </GhostButton>
                            
                            {/* Skip option */}
                            <button
                                onClick={() => {
                                    setUserData(prev => ({ ...prev, genderIdentity: 'prefer-not-to-say' }));
                                    handleNextStep();
                                }}
                                className="mt-4 text-xs text-foreground/30 hover:text-foreground/50 transition-colors"
                            >
                                {t('skip')}
                            </button>
                        </motion.div>
                    )}

                    {/* Step 3: The Alignment (Birth Info) */}
                    {currentStep === 3 && (
                        <motion.div
                            key="alignment"
                            className="relative flex flex-col items-center justify-center text-center w-full max-w-lg px-4"
                            variants={questionVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ duration: 0.6 }}
                        >
                            {/* Armillary Sphere - 3D Golden Astrolabe */}
                            <StepBackButton onClick={() => setCurrentStep(2)} show={showBackButton} />
                            
                            <ArmillarySphere className="mb-4" />
                            
                            <h2 className="font-cinzel text-xl sm:text-2xl text-foreground mb-2">
                                {t('onboarding.birth_title')}
                            </h2>
                            <p className="font-inter text-sm text-muted-foreground mb-6 italic">
                                {t('onboarding.birth_subtitle')}
                            </p>
                            
                            <div className="w-full space-y-10 mb-8">
                                {/* Celestial Date Picker */}
                                <div className="pb-4">
                                    <div className="flex items-center justify-center gap-2 mb-4">
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                                        <span className="text-[10px] font-inter text-primary/50 uppercase tracking-[0.2em]">
                                            {t('onboarding.birth_date')}
                                        </span>
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                                    </div>
                                    <CelestialDatePicker
                                        value={userData.birthDate}
                                        onChange={(value) => setUserData(prev => ({ ...prev, birthDate: value }))}
                                    />
                                </div>
                                
                                {/* Birth Place */}
                                <div>
                                    <div className="flex items-center justify-center gap-2 mb-4">
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                                        <span className="text-[10px] font-inter text-primary/50 uppercase tracking-[0.2em]">
                                            {t('onboarding.birth_place')}
                                        </span>
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                                    </div>
                                    <LocationAutocomplete
                                        value={userData.location}
                                        onChange={(value) => {
                                            // LocationAutocomplete returns an object with { display, lat, lng, utc_offset_minutes }
                                            setUserData(prev => ({ 
                                                ...prev, 
                                                location: value,
                                                // Extract coordinates if available
                                                locationLat: value?.lat || prev.locationLat,
                                                locationLng: value?.lng || prev.locationLng,
                                                utcOffsetMinutes: value?.utc_offset_minutes ?? prev.utcOffsetMinutes
                                            }));
                                        }}
                                        placeholder={t('ui.city_country')}
                                    />
                                </div>
                                
                                {/* Celestial Time Picker */}
                                {!userData.unknownTime && (
                                    <motion.div
                                        className="pb-2"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                    >
                                        <div className="flex items-center justify-center gap-2 mb-4">
                                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                                            <span className="text-[10px] font-inter text-primary/50 uppercase tracking-[0.2em]">
                                                {t('onboarding.birth_time')}
                                            </span>
                                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                                        </div>
                                        <CelestialTimePicker
                                            value={userData.birthTime}
                                            onChange={(value) => setUserData(prev => ({ ...prev, birthTime: value }))}
                                        />
                                        <p className="mt-3 text-[10px] font-inter text-primary/40 tracking-wide">
                                            {t('ui.precision_unlocks')}
                                        </p>
                                    </motion.div>
                                )}
                                
                                {/* Unknown time toggle */}
                                <button
                                    onClick={() => setUserData(prev => ({ 
                                        ...prev, 
                                        unknownTime: !prev.unknownTime,
                                        birthTime: ""
                                    }))}
                                    className={`text-sm font-inter transition-colors ${
                                        userData.unknownTime ? "text-primary" : "text-muted-foreground hover:text-primary"
                                    }`}
                                    data-testid="unknown-time-toggle"
                                >
                                    {userData.unknownTime ? `✦ ${t('ui.remember_time')}` : t('ui.dont_know_time')}
                                </button>
                                
                                {/* Time of Day fallback for unknown time */}
                                {userData.unknownTime && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="pt-2"
                                    >
                                        <TimeOfDaySelector
                                            value={userData.timeOfDay}
                                            onChange={(value) => setUserData(prev => ({ ...prev, timeOfDay: value }))}
                                        />
                                    </motion.div>
                                )}
                            </div>
                            
                            <GhostButton 
                                onClick={handleNextStep}
                                disabled={!userData.birthDate || !userData.location?.display || (!userData.birthTime && !userData.timeOfDay)}
                                data-testid="alignment-continue-btn"
                            >
                                {t('continue')}
                            </GhostButton>
                        </motion.div>
                    )}

                    {/* Step 4: The Energy (Mood) - Inner Light Meditation */}
                    {currentStep === 4 && (
                        <motion.div
                            key="energy"
                            className="relative flex flex-col items-center justify-center text-center w-full max-w-md"
                            variants={questionVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ duration: 0.6 }}
                        >
                            <StepBackButton onClick={() => setCurrentStep(3)} show={showBackButton} />
                            
                            {/* Meditation Aura - pulsating indigo/violet */}
                            <div className="relative mb-6">
                                <MeditationAura />
                                
                                {/* Centered text overlay */}
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <motion.h2 
                                        className="font-cinzel text-xl sm:text-2xl text-foreground mb-2"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.5, duration: 1 }}
                                    >
                                        {t('tarot.close_your_eyes')}
                                    </motion.h2>
                                    <motion.p 
                                        className="font-inter text-sm text-foreground/60 italic"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 1, duration: 1 }}
                                    >
                                        {t('tarot.breathe_deeply')}
                                    </motion.p>
                                </div>
                            </div>
                            
                            <motion.p 
                                className="font-cinzel text-lg text-foreground/80 mb-6"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 1.5, duration: 0.8 }}
                            >
                                {t('onboarding.emotion_title')}
                            </motion.p>
                            
                            {/* Oracle Cards for mood selection */}
                            <motion.div 
                                className="w-full max-w-sm mb-8"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 2, duration: 0.6 }}
                            >
                                <OracleCards
                                    options={moodOptions}
                                    selected={userData.mood}
                                    onSelect={(value) => setUserData(prev => ({ ...prev, mood: value }))}
                                />
                            </motion.div>
                            
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 2.3, duration: 0.5 }}
                            >
                                <GhostButton 
                                    onClick={handleNextStep}
                                    disabled={!userData.mood}
                                >
                                    {t('continue')}
                                </GhostButton>
                            </motion.div>
                        </motion.div>
                    )}

                    {/* Step 5: The Focus - Floating Tarot Cards */}
                    {currentStep === 5 && (
                        <motion.div
                            key="focus"
                            className="relative flex flex-col items-center justify-center text-center w-full max-w-lg"
                            variants={questionVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ duration: 0.6 }}
                        >
                            <StepBackButton onClick={() => setCurrentStep(4)} show={showBackButton} />
                            
                            <h2 className="font-cinzel text-xl sm:text-2xl text-foreground mb-3">
                                {t('onboarding.shadow_title')}
                            </h2>
                            <p className="font-inter text-sm text-muted-foreground mb-8 italic">
                                {t('onboarding.shadow_subtitle')}
                            </p>
                            
                            {/* Floating Tarot Cards */}
                            <FloatingTarotCards
                                options={focusOptions}
                                selected={userData.focus}
                                onSelect={(value) => setUserData(prev => ({ ...prev, focus: value }))}
                            />
                            
                            <div className="mt-8">
                                <GhostButton 
                                    onClick={handleNextStep}
                                    disabled={!userData.focus}
                                >
                                    {t('continue')}
                                </GhostButton>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 6: Tarot of the Void */}
                    {currentStep === 6 && (
                        <motion.div
                            key="tarot-reader"
                            className="relative flex flex-col items-center justify-center text-center w-full"
                            variants={questionVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ duration: 0.6 }}
                        >
                            <StepBackButton onClick={() => setCurrentStep(5)} show={showBackButton} />
                            
                            <TarotReader 
                                userName={userData.name || "Child of the Stars"}
                                userContext={{
                                    gender: userData.genderIdentity,
                                    birthDate: userData.birthDate,
                                    birthTime: userData.birthTime,
                                    location: userData.location,
                                    mood: userData.mood,
                                    focus: userData.focus
                                }}
                                onComplete={(cards, reading) => {
                                    // Save tarot reading and proceed to Dashboard
                                    setUserData(prev => ({
                                        ...prev,
                                        tarotCards: cards,
                                        tarotReading: reading
                                    }));
                                    // After Tarot, check if user is authenticated
                                    // If guest, show Claim Destiny modal for SMS/Facebook login
                                    const isAuthenticated = localStorage.getItem('session_token');
                                    if (!isAuthenticated) {
                                        console.log('👤 Guest user after Tarot - showing Claim Destiny modal');
                                        setShowClaimModal(true);
                                    } else {
                                        setShowMarketplace(true);
                                    }
                                }}
                                onBack={() => setCurrentStep(prev => Math.max(0, prev - 1))}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Reset button (for testing) - shown on questions */}
            {currentStep > 0 && currentStep <= 5 && (
                <motion.button
                    onClick={resetOnboarding}
                    className="fixed bottom-6 left-6 px-4 py-2 text-xs text-muted-foreground border border-muted hover:border-primary hover:text-primary transition-colors rounded z-20"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                >
                    Reset
                </motion.button>
            )}

            {/* Claim Destiny Modal - shown after calibration for guest users or for returning users signing in */}
            <ClaimDestinyModal
                isOpen={showClaimModal}
                onClose={handleClaimModalClose}
                onSkipAsGuest={handleSkipClaim}
                onSuccess={handleClaimSuccess}
                guestData={userData}
                isReturningUser={isReturningUser}
            />
        </div>
    );
};

export default OnboardingFlow;
